import styled from '@emotion/styled'
import classNames from 'classnames'

import { Divider, Switch } from 'antd'
import { useEffect, useMemo, useRef, useState } from 'react'
import { usePageModal } from '@/components'
import { Editor } from '@/pages/datastores/document-detail/paragraph/editor'
import { ImagePreviewModal } from '@/components/modal/ImagePreviewModal'
import { useModal } from '@/hooks/useModal.tsx'

import { QAParagraphItemEditorModal } from '../ParagraphItemEditorModal'
import KeywordsView from '@/pages/datastores/components/KeywordsView'
import type { ParagraphListItemProps } from './CommonParagraphListItem'
import {
  OperationAction,
  OperationActionType,
} from './components/OperationAction'

const Wrap = styled.div<{ disabled: boolean; isEdited?: boolean }>`
  .tags {
    display: none;
  }
  &:hover {
    .info-box,
    .delete-btn {
      opacity: 1;
      width: auto;
    }
    .num-text {
      display: none;
    }
    .tags {
      display: block;
    }
  }

  .paragraph-editor,
  .paragraph-editor > *:not(.paragraph-split-mark) {
    ${props => props.disabled && 'color: rgba(141, 141, 153, 0.6);'}
  }

  .edited-mark:before {
    content: '';
    width: 24px;
    height: 24px;
    background: #38a6ff;
    opacity: 0.4;
    position: absolute;
    top: -12px;
    left: -12px;
    pointer-events: none;
    display: ${props => (props.isEdited ? 'block' : 'none')};
    transform: rotate(45deg);
  }

  @keyframes show-qa-paragraph-active {
    0% {
      background-color: hsla(240deg, 0%, 100%, 1);
    }

    20% {
      background-color: hsla(240deg, 7%, 90%, 1);
    }

    100% {
      background-color: hsla(240deg, 0%, 100%, 1);
    }
  }

  .paragraph-blink {
    animation: show-qa-paragraph-active linear 1s 2;
  }
`

export default (props: ParagraphListItemProps) => {
  const {
    first,
    border,
    readonly,
    paragraph,
    numText,
    onEnableChange,
    onDelete,
    ossClient,
    file_id,
    activeId,
    rank,
    onEditorLoad,
    onEditorDestroy,
    updateParagraphsItemById,
    addParagraphsItem,
    onSelect,
  } = props

  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)
  const editorRef = useRef<HTMLDivElement | null>(null)
  const editorInterface = useRef<Editor>()
  const pageModal = usePageModal()

  const [qaParagraphEditorModal] = useModal(QAParagraphItemEditorModal)

  const [focus, setFocus] = useState(false)

  const [imagePreviewModal] = useModal(ImagePreviewModal)

  const onImageUpload = async (file: File) => {
    if (ossClient) {
      const filePath = `i/${file_id}/${+new Date()}/${file.name}`

      ossClient.useBucket(import.meta.env.VITE_AI_DATASTORE_RESOURCE_BUCKET)
      const res = await ossClient.put(filePath, file)

      return res.url
    }
    throw new Error('oss client is not init')
  }

  const handleSelect = () => {
    onSelect?.((paragraph.chunk_id || (paragraph as any).originalChunkId) ?? 0)
  }

  const loadEditor = () => {
    editorInterface.current = new Editor({
      dom: editorRef.current!,
      content: paragraph.content,
      onImageUpload,
      editEnable: false,
    })
    onEditorLoad(editorInterface.current)
    editorInterface.current.init()
    editorInterface.current.registryEvents('onFocus', () => setFocus(true))
    editorInterface.current.registryEvents('onBlur', () => setFocus(false))
    editorInterface.current.registryEvents('onLinkOpen', (url: string) => {
      pageModal.show({
        url,
      })
    })
    editorInterface.current.registryEvents('onImagePreview', (url: string) => {
      imagePreviewModal.open({ url })
    })
  }

  const handleOpenModal = () => {
    qaParagraphEditorModal.open({
      operationType: 'update',
      file_id,
      chunk_id: paragraph.chunk_id!,
      paragraph,
      title: `问答${rank || Number(paragraph?.key) + 1}`,
      callback: updateParagraphsItemById!,
    })
  }

  const addParagraph = () => {
    qaParagraphEditorModal.open({
      operationType: 'add',
      file_id: file_id!,
      insert_above_chunk_id: paragraph.chunk_id,
      paragraph: {
        content: '',
        enable: true,
        extra_info: { 答案: '' },
      },
      title: '新增问答',
      callback: values => {
        addParagraphsItem?.(values, paragraph.chunk_id)
      },
    })
  }

  const handleActionClick = (type: OperationActionType) => {
    switch (type) {
      case OperationActionType.ADD:
        addParagraph()
        break
      case OperationActionType.DELETE:
        onDelete(paragraph.key)
        break
      case OperationActionType.EDIT:
        handleOpenModal()
        break
    }
  }

  useEffect(() => {
    loadEditor()
    return () => {
      editorInterface.current?.destroy()
      onEditorDestroy()
    }
  }, [])

  const actionList = useMemo(() => {
    return Object.values(OperationActionType).filter(
      item => item !== OperationActionType.AI,
    )
  }, [])

  useEffect(() => {
    if (activeId !== paragraph.chunk_id) return
    handleSelect()
  }, [activeId])

  const extraInfoContent = useMemo(() => {
    const extra_info = paragraph?.extra_info as any
    const keys = Object.keys(extra_info as any)
    const key = keys[0]
    return extra_info?.[key]
  }, [paragraph?.extra_info])

  return (
    <Wrap
      disabled={true}
      isEdited={paragraph.isEdited}
      onClick={() => {
        handleActionClick(OperationActionType.EDIT)
      }}
      className={classNames(
        'ml-24 relative rounded-8px overflow-hidden line-height-20px',
        {
          'mt-20px': !first,
          '!hover:shadow-[0px_8px_24px_0px_rgba(23,23,29,0.12)]': true,
        },
      )}
    >
      <div className='edited-mark w-full h-full absolute left-0 top-0 overflow-hidden rounded-8px pointer-events-none'></div>
      <div
        className={classNames(
          'px-20 py-16 bg-white rounded-8px cursor-pointer',
          {
            'border-0': !border,
            border,
            'hover:b-primary': !readonly && border,
            'focus-within:b-primary': !readonly && border,
            '!b-error': deleteConfirmOpen,
            'paragraph-blink': activeId === paragraph.chunk_id,
          },
        )}
      >
        <div
          className='flex-center-between gap-8'
          onClick={e => e.stopPropagation()}
        >
          <div className='flex overflow-hidden'>
            <div
              className={classNames(
                'num-text text-12px c-font_1 font-400 line-height-20px h-26px',
              )}
            >
              {numText}
            </div>
            <div className={classNames('flex items-center tags w-full', {})}>
              <KeywordsView
                value={paragraph.keywords ?? []}
                className='flex flex-1'
              />
            </div>
          </div>
          <div className='flex items-center'>
            <div
              className={classNames('flex items-center info-box', {
                'op-0': !focus,
              })}
            >
              {(Object.keys(paragraph?.extra_info || {}).length && (
                <span className='c-#8D8D99 text-12px line-height-12px  whitespace-nowrap'>
                  共 {Object.keys(paragraph?.extra_info || {}).length} 回答
                </span>
              )) ||
                null}
              {!paragraph.enable && (
                <div className='ml-12 py-2 text-12px px-8 rounded-4px c-#FE9700 bg-#FE9700 bg-op-12'>
                  <span className='w-8px h-8px rounded-full mr-6 inline-block bg-#FE9700'></span>
                  <span>已禁用</span>
                </div>
              )}
              {!readonly && (
                <>
                  <Divider type='vertical' className='mx-12' />
                  <Switch
                    size='small'
                    checked={paragraph.enable}
                    onChange={onEnableChange}
                  />
                </>
              )}
            </div>
            {!readonly && (
              <span
                className={classNames('flex items-center delete-btn', {
                  'op-0': !focus && !deleteConfirmOpen,
                  'w-0': !deleteConfirmOpen,
                })}
              >
                <Divider type='vertical' className='mx-12' />
                <OperationAction
                  actionList={actionList}
                  onActionClick={handleActionClick}
                  deleteConfirmOpen={deleteConfirmOpen}
                  setDeleteConfirmOpen={setDeleteConfirmOpen}
                />
              </span>
            )}
          </div>
        </div>

        <div
          className={classNames('flex flex-col', {
            'op-40': !paragraph.enable,
          })}
        >
          <div className='mt-14px flex items-center'>
            <p className='bg-[rgba(123,97,255,0.12)] c-#7B61FF text-12px line-height-20px font-500  px-4px rounded-4px'>
              问
            </p>
            <p className='ml-8px text-14px line-height-20px font-500 c-#17171D text-one-line'>
              {paragraph?.content}
            </p>
          </div>
          <div className='mt-14px w-100% overflow-hidden flex items-center'>
            <p className='bg-[rgba(56,166,255,0.12)] c-#38A6FF text-12px line-height-20px font-500 px-4px rounded-4px'>
              答
            </p>
            <p className='ml-8px text-14px line-height-20px truncate flex-1'>
              {extraInfoContent}
            </p>
            <div
              className='h-0px w-0 invisible mt-12 ml-8px text-14px line-height-20px'
              ref={editorRef}
            />
          </div>
        </div>
      </div>
    </Wrap>
  )
}
